<template>
	<div :style="`height: ${props.setHeight}px`">
		<div ref="mapContainer" class="map-container" :style="`height: ${props.setHeight}px`">
			<div class="asset-element-card-container px-100">
				<AssetElementCard v-if="showCard && selectedAssetElement" :asset-element="selectedAssetElement" />
			</div>
		</div>
	</div>
</template>

<script lang="ts">
export default {
	name: 'AssetElementMap'
};
</script>

<script setup lang="ts">
import { computed, onMounted, reactive, Ref, ref, watch } from 'vue';
import mapboxgl, { LngLatLike, Popup } from 'mapbox-gl';
import { Asset } from 'types/assets';
import AssetElementCard from './asset-element-card.vue';
import { useAppStore } from '@stores/app';
import type { Feature, FeatureCollection } from 'geojson';

interface Props {
	assetElements: Array<Asset>;
	customZoom?: number;
	setHeight?: number;
}

const props = withDefaults(defineProps<Props>(), {
	customZoom: 16,
	setHeight: 500
});

const appStore = useAppStore();

let currentPopup!: Popup;

let map: mapboxgl.Map;

const selectedAssetElement: Ref<Asset | null> = ref(null);

const showCard = ref(false);

const geoJsonPoints: FeatureCollection = reactive({
	type: 'FeatureCollection',
	features: []
});

const nonPolygonPoints: FeatureCollection = reactive({
	type: 'FeatureCollection',
	features: []
});

const mapContainer: Ref<HTMLElement | null> = ref(null);

const isSingleAssetElement = computed((): boolean => props.assetElements.length === 1);

const accessToken = computed((): string | undefined =>
	appStore.isAuthenticated ? import.meta.env.VITE_MAPBOX_TOKEN : import.meta.env.VITE_MAPBOX_ANONYMOUS_SITE_TOKEN
);

watch(
	() => props.assetElements,
	() => {
		if (currentPopup && currentPopup.isOpen()) {
			currentPopup.remove();
		}

		populateMap();
	}
);

function addAssetElementToMapData(assetElement: Asset): void {
	if (!assetElement.realEstateAndPrivateCreditFields?.latitude) return;

	const latNum = Number(assetElement.realEstateAndPrivateCreditFields.latitude);
	const lonNum = Number(assetElement.realEstateAndPrivateCreditFields.longitude);

	const pointFeature: Feature = {
		type: 'Feature',
		properties: {
			assetElement
		},
		geometry: {
			type: 'Point',
			coordinates: [lonNum, latNum]
		}
	};

	geoJsonPoints.features.push(pointFeature);
	nonPolygonPoints.features.push(pointFeature);
}

function closeElementCard(): void {
	showCard.value = false;
}

function populateMap(): void {
	resetMap();
	closeElementCard();

	geoJsonPoints.features = [];

	props.assetElements.forEach((assetElement) => {
		addAssetElementToMapData(assetElement);
	});

	map.addSource('allPoints', {
		type: 'geojson',
		data: geoJsonPoints,
		cluster: true,
		clusterMaxZoom: 10,
		clusterRadius: 25
	});

	map.addSource('nonPolygonsPointSource', {
		type: 'geojson',
		data: nonPolygonPoints
	});

	map.addLayer({
		id: 'clusters',
		source: 'allPoints',
		type: 'circle',
		filter: ['has', 'point_count'],
		paint: {
			'circle-color': '#AA4528',
			'circle-radius': {
				property: 'point_count',
				type: 'interval',
				stops: [
					[3, 10],
					[4, 11],
					[5, 12],
					[6, 13],
					[7, 14]
				]
			},
			'circle-stroke-color': '#F9B19C',
			'circle-stroke-opacity': 0.5,
			'circle-stroke-width': [
				'interpolate',
				['linear'],
				['zoom'],
				0,
				['/', 1.5, ['get', 'point_count']],
				10,
				['get', 'point_count']
			]
		}
	});

	map.addLayer({
		id: 'clusterCount',
		source: 'allPoints',
		type: 'symbol',
		filter: ['has', 'point_count'],
		layout: {
			'text-field': '{point_count_abbreviated}',
			'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
			'text-size': 12
		},
		paint: {
			'text-color': '#ffffff'
		}
	});

	map.addLayer({
		id: 'points',
		source: 'allPoints',
		type: 'circle',
		paint: {
			'circle-color': '#AA4528',
			'circle-radius': 6
		},
		filter: ['!has', 'point_count'],
		maxzoom: 14
	});

	map.addLayer({
		id: 'nonPolygonPoints',
		source: 'nonPolygonsPointSource',
		type: 'circle',
		paint: {
			'circle-color': ['get', 'color'],
			'circle-radius': 6
		},
		minzoom: 14,
		filter: ['!has', 'point_count']
	});

	setupClickHandlers();
	setupHoverHandlers();
}

function setupClickHandlers(): void {
	map.on('click', 'clusters', (e) => {
		map.flyTo({
			center: e.lngLat,
			zoom: map.getZoom() + 2
		});
	});

	/* eslint-disable @typescript-eslint/ban-ts-comment */
	map.on('click', 'points', (e) => {
		if (e.features) {
			const point = e.features[0];
			if (point.properties) {
				const assetElement = JSON.parse(point.properties.assetElement) as Asset;
				// @ts-ignore
				openClickItems(point.geometry.coordinates, assetElement);
				e.preventDefault();
			}
		}
	});

	map.on('click', 'nonPolygonPoints', (e) => {
		if (e.features) {
			const point = e.features[0];
			if (point.properties) {
				const assetElement = JSON.parse(point.properties.assetElement) as Asset;
				// @ts-ignore
				openClickItems(point.geometry.coordinates, assetElement);
				e.preventDefault();
			}
		}
	});
	/* eslint-enable @typescript-eslint/ban-ts-comment */

	map.on('click', (e) => {
		if (e.defaultPrevented === false) {
			showCard.value = false;
		}
	});
}

function setupHoverHandlers(): void {
	map.on('mouseover', 'clusters', () => {
		handleMouseOver();
	});

	map.on('mouseleave', 'clusters', () => {
		handleMouseLeave();
	});

	map.on('mouseover', 'clusterCount', () => {
		handleMouseOver();
	});

	map.on('mouseleave', 'clusterCount', () => {
		handleMouseLeave();
	});

	map.on('mouseover', 'points', () => {
		handleMouseOver();
	});

	map.on('mouseleave', 'points', () => {
		handleMouseLeave();
	});

	map.on('mouseover', 'nonPolygonPoints', () => {
		handleMouseOver();
	});

	map.on('mouseleave', 'nonPolygonPoints', () => {
		handleMouseLeave();
	});
}

function handleMouseOver(): void {
	map.getCanvas().style.cursor = 'pointer';
}

function handleMouseLeave(): void {
	map.getCanvas().style.cursor = '';
}

function resetMap(): void {
	const sources = ['allPoints', 'nonPolygonsPointSource'];
	const layers = ['clusters', 'clusterCount', 'points', 'nonPolygonPoints'];
	layers.forEach((layer) => {
		if (map.getLayer(layer)) {
			map.removeLayer(layer);
		}
	});
	sources.forEach((source) => {
		if (map.getSource(source)) {
			map.removeSource(source);
		}
	});
}

function openClickItems(coordinates: LngLatLike, assetElement: Asset): void {
	if (!isSingleAssetElement.value) {
		map.flyTo({
			center: coordinates,
			offset: [0, 0]
		});

		selectedAssetElement.value = assetElement;
		showCard.value = true;
	}

	if (assetElement.realEstateAndPrivateCreditFields?.streetAddress) {
		currentPopup = new mapboxgl.Popup({ closeButton: false })
			.setLngLat(coordinates)
			.setHTML(`<p>${formatFullAddress(assetElement)}</p>`)
			.addTo(map) as Popup;
	}
}

function formatFullAddress(asset: Asset): string {
	const address = asset.realEstateAndPrivateCreditFields?.streetAddress;
	const city = asset.realEstateAndPrivateCreditFields?.city
		? `<br>${asset.realEstateAndPrivateCreditFields.city}`
		: '';
	const state = asset.realEstateAndPrivateCreditFields?.state
		? `, ${asset.realEstateAndPrivateCreditFields.state}`
		: '';
	const zip = asset.realEstateAndPrivateCreditFields?.zipCode
		? ` ${asset.realEstateAndPrivateCreditFields.zipCode}`
		: '';

	return `${address}${city}${state}${zip}`;
}

onMounted(() => {
	let initZoom = 0;
	let centerLatLong: LngLatLike = [-98.716052, 39.0281178];

	if (isSingleAssetElement.value) {
		const assetElement = props.assetElements.find((a) => !!a.realEstateAndPrivateCreditFields?.latitude);

		if (
			assetElement &&
			assetElement.realEstateAndPrivateCreditFields?.longitude &&
			assetElement.realEstateAndPrivateCreditFields?.latitude
		) {
			centerLatLong = [
				Number(assetElement.realEstateAndPrivateCreditFields.longitude),
				Number(assetElement.realEstateAndPrivateCreditFields.latitude)
			];
			initZoom = props.customZoom;
		}
	} else {
		initZoom = 3;
	}

	if (mapContainer.value) {
		map = new mapboxgl.Map({
			accessToken: accessToken.value,
			container: mapContainer.value,
			style: 'mapbox://styles/mapbox/light-v9',
			center: centerLatLong,
			zoom: initZoom
		});
	}

	map.once('load', () => {
		populateMap();
		map.resize();
		map.addControl(new mapboxgl.NavigationControl(), 'top-left');
		map.addControl(new mapboxgl.FullscreenControl(), 'top-left');
	});
});
</script>

<style lang="scss">
@use '../../styles/utilities/respond-to.scss' as *;
@import 'mapbox-gl/dist/mapbox-gl';

.map-container {
	flex: 1;
}

.asset-element-card-container {
	bottom: 1rem;
	left: 50%;
	position: absolute;
	transform: translateX(-50%);
	width: 100%;
	z-index: 1;

	@include respond-to(md) {
		width: 87.5%;
	}

	@include respond-to(lg) {
		width: 75%;
	}
}

.mapboxgl-ctrl-group .mapboxgl-ctrl-compass,
a.mapboxgl-ctrl-logo {
	display: none;
}
</style>
